import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { FormBuilder, FormGroup, FormControl, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, scan, takeUntil, tap } from 'rxjs/operators';
import { MatOptionSelectionChange } from '@angular/material/core';
import { VendorComboDTO } from '@app/requests/model/combos-model';
import { REQUEST_MODE_NEW } from '@app/@core/constants/requests.constants';

@Component({
  selector: 'scf-custom-select-search',
  templateUrl: './custom-select-search.html',
  styleUrls: ['./custom-select-search.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectSearchComponent),
      multi: true,
    },
  ],
})
export class CustomSelectSearchComponent implements OnInit, OnDestroy, ControlValueAccessor, OnChanges {
  @Input() cucumberId: string;
  @Input() dataInput: any[] = [];
  @Input() establishmentValue: string = '';
  @Input() placeholder: string = '';
  @Input() isRequired: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() hasBlur: boolean = false;
  @Input() id: string = '';
  @Input() isMultiple: boolean = false;
  @Input() isCTI: boolean = false;
  @Input() hasSelectOptionAll: boolean = false;
  @Input() hasSelectOptionEmpty: boolean = false;
  @Input() hasToTranslateValue: boolean = false;
  @Input('showErrors') set showErrors(data: any) {
    if (data?.required) {
      this.formSelector?.controls?.selectorCustom?.setErrors(data ? data : null);
    } else if (data?.showErrorCalculate) {
      this.formSelector?.controls?.selectorCustom?.markAllAsTouched();
    } else if (data === true) {
      this.formSelector?.controls?.selectorCustom?.setErrors({ showErrorCalculate: true });
      this.formSelector?.controls?.selectorCustom?.markAllAsTouched();
    } else if (data?.avalistaError) {
      this.formSelector?.controls?.selectorCustom?.setErrors({ avalistaError: true });
      this.formSelector?.controls?.selectorCustom?.markAllAsTouched();
    }
  }
  @Input() selectorIdProperty: string = 'id';
  @Input() selectorValueProperty: string = 'value';
  @Input() public mode: string = REQUEST_MODE_NEW;

  @Output() onBlurTriggered = new EventEmitter<any>();
  @Output() onSelectCheckAll = new EventEmitter<{ id: string; value: boolean }>();
  @Output() selectionChange = new EventEmitter<any>();
  @Output() searchChange = new EventEmitter<any>();

  @ViewChild('matSelectInfiniteScroll', { static: true })
  infiniteScrollSelect: MatSelect;

  private selectedValuesOnMultiselect: any[] = [];

  private _data = new BehaviorSubject<any[]>(this.dataInput);
  private data$ = this._data.asObservable();
  isToogledAllChecked = true;
  isVisible: boolean = true;
  searchCtrl: FormControl = new FormControl();
  selectorCustom: FormControl = new FormControl();
  hideSearch: boolean = false;
  formSelector: FormGroup;

  onChangeForm: Subscription[] = [];
  onTouch = () => {};

  _options = new BehaviorSubject([]);
  options$ = this._options.asObservable().pipe(
    scan((acc, curr) => {
      if (!acc || !curr) {
        return [];
      }
      let dataArr = new Set([...acc, ...curr]);
      return [...dataArr];
    }, [])
  );
  offset = 0;
  limit = 20;
  /**
   * Holds the option matches
   * a subset of this data is selected
   * using the offset and limit
   */
  data: any[] = [];

  /** Subject that emits when the component has been destroyed. */
  protected _onDestroy = new Subject<void>();
  isVendor: boolean = false;

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.formSelector = this.fb.group(
      {
        selectorCustom: [''],
      },
      { updateOn: this.isMultiple ? 'blur' : 'change' }
    );
    this.isVendor = this.id === 'vendor';
    this.data$.pipe(takeUntil(this._onDestroy)).subscribe({
      next: (data) => {
        //console.log(data);
        //console.log('Ingested data changed');
        this.data = data;
        this.offset = 0;
        this._options.next(null);
        this.getNextBatch();
      },
    });

    const searchValueChange$ = this.searchCtrl.valueChanges;
    const selectOpenChange$ = this.infiniteScrollSelect.openedChange;
    combineLatest([selectOpenChange$, searchValueChange$])
      .pipe(
        debounceTime(500),
        filter(([isOpen]) => isOpen === true && !this.selectorCustom.value),
        takeUntil(this._onDestroy)
      )
      .subscribe(([, value]) => this.onSearchChange(value));

    // this.options$.pipe(takeUntil(this._onDestroy)).subscribe((val) => {
    //console.log(`New view array contains ${val.value} items`);
    // });

    this.infiniteScrollSelect._closedStream.subscribe(() => {
      this.isVisible = true;
      this.sortArrayElements(this.dataInput);
    });

    this.isDisabled ? this.formSelector.disable() : this.formSelector.enable();

    if (this.isMultiple) {
      // console.log('setCheckAll', { id: this.id, value: this.isToogledAllChecked });
      this.onSelectCheckAll.emit({ id: this.id, value: this.isToogledAllChecked });
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      this.isMultiple &&
      !changes?.dataInput?.isFirstChange() &&
      changes?.dataInput?.currentValue?.length !== changes?.dataInput?.previousValue?.length
    ) {
      this.isToogledAllChecked = true;
      this.selectedValuesOnMultiselect = [];
      // this.formSelector.controls.selectorCustom.setValue(this.dataInput.map((id) => id))
      // console.log('onChanges - setCheckAll', changes?.dataInput);
      // console.log('onChanges - setCheckAll', { id: this.id, value: this.isToogledAllChecked });
      this.onSelectCheckAll.emit({ id: this.id, value: this.isToogledAllChecked });
    }

    if (this.dataInput) {
      //console.log('ngOnChanges', this.dataInput);
      this._data.next(this.dataInput);
      const results = this.dataInput?.slice(this.offset, this.offset + this.limit);
      //en el caso de modo vista o edición, si el dato que se seleccionó no esta entre los primeros, se ha de poner el primero de la lista para que salga
      if (this.formSelector?.controls?.selectorCustom !== undefined) {
        let object = this.dataInput.find(
          (valor) => valor[this.selectorIdProperty] === this.formSelector?.controls?.selectorCustom?.value
        );
        if (object !== undefined) {
          results.unshift(object);
        }
      }
      this._options.next(results);
      this.offset += this.limit;
      this.data = this.dataInput;

      if (this.establishmentValue) {
        let value;
        const foundValue = this.data.find(
          (stablishment) => stablishment[this.selectorIdProperty] === this.establishmentValue
        );
        if (foundValue) {
          value = foundValue[this.selectorValueProperty];
        }
        this.onSearchChange(value);
      }
    }
  }

  onChange(e: any): void {
    // console.log(`Value Changed: ${e}`);
    // console.log('controls: ', this.formSelector.controls);
    this.onBlurTriggered.emit(true);
  }

  sortArrayElements(array: any) {
    const selectorCustomValue = this.formSelector.controls.selectorCustom.value;
    if (selectorCustomValue && (typeof selectorCustomValue === 'string' || Array.isArray(selectorCustomValue))) {
      let result: any = array.sort((a: any, b: any) => {
        if (
          selectorCustomValue.includes(a[this.selectorIdProperty]) &&
          selectorCustomValue.includes(b[this.selectorIdProperty])
        ) {
          return a[this.selectorValueProperty].toLowerCase() < b[this.selectorValueProperty].toLowerCase() ? -1 : 1;
        } else if (selectorCustomValue.includes(a[this.selectorIdProperty])) {
          return -1;
        } else if (selectorCustomValue.includes(b[this.selectorIdProperty])) {
          return 1;
        } else {
          return a[this.selectorValueProperty].toLowerCase() < b[this.selectorValueProperty].toLowerCase() ? -1 : 1;
        }
      });
      this._data.next(result);
    } else {
      this._data.next(array);
    }
  }

  onSearchChange(e: any): void {
    console.log(`Search Changed: ${e}`);
    this.data = [];
    this._options.next(null);
    this.searchChange.emit(e);

    let val = e ? e.trim() : null;
    var array: any = [];
    this.data.forEach((val) => {
      if (this.selectedValuesOnMultiselect.includes(val.id)) {
        array.push(val);
      }
    });
    if (!val) {
      this.isVisible = true;
      // console.log('data inpout onsearchange:', this.data);
      // Empty search, returns everything
      this.sortArrayElements(this.dataInput);
      this.hideSearch = false;
      return;
    } else {
      this.hideSearch = true;
      this.isVisible = false;
      val = val.toLowerCase();
    }

    var matches = this.dataInput.filter(
      (i) => i[this.selectorValueProperty]?.toLowerCase().indexOf(val.toLowerCase()) > -1
    );
    const setData = new Set(array.concat(matches));
    let result = [...setData];
    console.warn('id seleccionados: ' + this.selectedValuesOnMultiselect);
    //this._data.next(array.concat(matches));
    this.sortArrayElements(result);
  }

  /**
   * Used for selecting next batch
   * for ngx-infinite-scroll
   */
  getNextBatch(): void {
    // console.log('get next batch', this.selectedValuesOnMultiselect);
    if (this.isMultiple && this.selectedValuesOnMultiselect?.length > 0) {
      this.formSelector?.get('selectorCustom')?.setValue(this.selectedValuesOnMultiselect, { emitEvent: false });
    }
    const results = this.data?.slice(this.offset, this.offset + this.limit);
    this._options.next(results);
    this.offset += this.limit;
  }

  writeValue(obj: any): void {
    this.formSelector?.get('selectorCustom')?.setValue(obj, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    const sub = this.formSelector?.get('selectorCustom')?.valueChanges.subscribe(fn);
    this.onChangeForm.push(sub);
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean) {
    isDisabled
      ? this.formSelector?.controls.selectorCustom.disable()
      : this.formSelector?.controls.selectorCustom.enable();
  }

  toggleAllSelection = (value: boolean) => {
    // console.log('toggleAllSelection: ', value);
    this.onSelectCheckAll.emit({ id: this.id, value: value });
    this.isToogledAllChecked = value;
    const formSelect = this.formSelector.get('selectorCustom');
    value ? formSelect?.setValue(this.data.map((value) => value[this.selectorIdProperty])) : formSelect?.setValue([]);
    // console.log(formSelect.value);
    this.selectedValuesOnMultiselect = [];
  };

  onSelectionOptionChange = (event: MatOptionSelectionChange, value: any, data?: any) => {
    if (value) {
      const isToogledAllCheckedPrevious = this.isToogledAllChecked;

      this.isToogledAllChecked = this.formSelector.get('selectorCustom')?.value?.length === this.dataInput?.length;

      if (
        (!isToogledAllCheckedPrevious && this.isToogledAllChecked) ||
        (isToogledAllCheckedPrevious && !this.isToogledAllChecked)
      ) {
        this.onSelectCheckAll.emit({ id: this.id, value: this.isToogledAllChecked });
      }
    }
    if (this.isCTI) {
      this.onSelectCheckAll.emit(data);
    }
  };

  onSelectionChange = (selectChange: MatSelectChange) => {
    console.log('onSelectionChange', selectChange);
    this.selectionChange.emit(selectChange);
    if (this.isMultiple) {
      const value = selectChange?.value;
      this.selectedValuesOnMultiselect = value?.length > 0 ? [...value] : [];
    }
  };

  getValue(vendor: VendorComboDTO, value: any, id: any) {
    if (this.mode === REQUEST_MODE_NEW && this.isVendor) {
      return vendor;
    }
    if (this.isCTI) {
      return value;
    }
    return id;
  }

  _keydown(e: any) {
    //erase the value for searching
    if (e.code == 'Tab') this.searchCtrl.setValue('');
  }
}
